import React, { useEffect, useCallback, Fragment, useState, useRef } from "react";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import SaveIcon from "@mui/icons-material/Save";
import { useOutletContext, useParams, useSearchParams } from "react-router-dom";
import AllMainTreatmentTr from "./AllMainTreatmentTr";
import baseUrl from "../../../../../api/baseUrl";
import TreatmentFormModal from "../treatmentform/TreatmentFormModal";
import CaseTreatmentSection from "./CaseTreatmentSection";
import DateWiseTreatments from "./DateWiseTreatments";
import {
  isTreatmentItemEditable,
  isTreatmentItemPaid,
} from "./treatmentItemUtils";
import {
  validateTreatmentForm,
  type TreatmentFormData,
} from "../treatmentform/treatmentFormUtils";
import { toast } from "react-toastify";
import getDecryptedToken from "../../../../../api/getDecryptedToken";
import type { AddTreatmentOutletContext } from "../../../addTreatmentOutletContext";
import { appointmentIdPayload } from "../../../../../utils/appointmentLinkContext";

const DEFAULT_MAIN_TREATMENT_STATUS = "Complete";

const createDraftId = () =>
  `draft-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

interface TreatmentsProps {
  allTeatment: any[];
  setallTeatment: (value: any) => void;
  fetchData: () => void;
  registerSavePending?: (saveFn: (() => Promise<boolean>) | null) => void;
  registerHasPending?: (pending: boolean) => void;
  showDateWise?: boolean;
}

const Treatments: React.FC<TreatmentsProps> = ({
  allTeatment,
  setallTeatment,
  fetchData,
  registerSavePending,
  registerHasPending,
  showDateWise = false,
}) => {
  const { ID } = useParams<{ ID: string }>();
  const [searchParams] = useSearchParams();
  const appointmentId = String(searchParams.get("appointmentId") ?? "").trim();
  const { setHasPendingInput } =
    useOutletContext<AddTreatmentOutletContext>() ?? {};
  const [draftFormIds, setDraftFormIds] = useState<string[]>([]);
  const [isSavingDrafts, setIsSavingDrafts] = useState(false);
  const draftFormDataRef = useRef<Record<string, TreatmentFormData>>({});
  const [editingTreatmentId, setEditingTreatmentId] = useState<string | null>(
    null
  );
  const [openSubTreatmentId, setOpenSubTreatmentId] = useState<string | null>(
    null
  );

  useEffect(() => {
    const pending =
      draftFormIds.length > 0 ||
      !!editingTreatmentId ||
      !!openSubTreatmentId;
    setHasPendingInput?.(pending);
    registerHasPending?.(pending);
    return () => {
      setHasPendingInput?.(false);
      registerHasPending?.(false);
    };
  }, [
    draftFormIds.length,
    editingTreatmentId,
    openSubTreatmentId,
    setHasPendingInput,
    registerHasPending,
  ]);

  const editingTreatment = allTeatment?.find(
    (item: any) => String(item._id) === String(editingTreatmentId)
  );

  const getData = useCallback(
    (id: string) => {
      baseUrl
        .get("treatment/find-treatment", {
          headers: { treatmentid: id },
        })
        .then((response) => {
          setallTeatment(response.data.data.treatment);
        })
        .catch((error) => {
          console.error("Error fetching treatment data:", error);
        });
    },
    [setallTeatment]
  );

  useEffect(() => {
    if (ID) {
      getData(ID);
    }
  }, [ID, getData]);

  const removediagnose = useCallback(
    (e: any) => {
      if (e._id) {
        setallTeatment((prevTreatments: any) =>
          prevTreatments.filter((treatment: any) => treatment._id !== e._id)
        );

        baseUrl
          .post(
            "treatment/remove-treatments",
            {},
            {
              headers: {
                treatmentid: ID,
                treatmentsid: e._id,
              },
            }
          )
          .then(() => {
          })
          .catch((error) => {
            console.error("Error removing treatment:", error);
          });
      } else {
        setallTeatment((prevTreatments: any) =>
          prevTreatments.filter((treatment: any) => treatment.tid !== e.tid)
        );
      }
    },
    [ID, setallTeatment]
  );

  const refresh = useCallback(() => {
    if (ID) {
      getData(ID);
    }
  }, [ID, getData]);

  const handleEditTreatment = useCallback((item: any) => {
    if (!item?._id) {
      toast.error("Please refresh and try again");
      refresh();
      return;
    }
    const isSuperAdmin = getDecryptedToken("role") === "Super Admin";
    if (isTreatmentItemPaid(item) && !isSuperAdmin) {
      toast.error("Paid treatment cannot be edited");
      return;
    }
    if (!isTreatmentItemEditable(item, isSuperAdmin)) return;
    setOpenSubTreatmentId(null);
    setEditingTreatmentId(String(item._id));
  }, [refresh]);

  const closeEditForm = useCallback(() => {
    setEditingTreatmentId(null);
  }, []);

  const handleDraftChange = useCallback(
    (draftId: string, formData: TreatmentFormData) => {
      draftFormDataRef.current[draftId] = formData;
    },
    []
  );

  const handleDraftRemove = useCallback((draftId: string) => {
    delete draftFormDataRef.current[draftId];
    setDraftFormIds((prev) => prev.filter((id) => id !== draftId));
  }, []);

  const addDraftForm = useCallback(() => {
    setEditingTreatmentId(null);
    setOpenSubTreatmentId(null);
    setDraftFormIds((prev) => [...prev, createDraftId()]);
  }, []);

  const saveAllPendingTreatments = useCallback(async (): Promise<boolean> => {
    if (!ID || draftFormIds.length === 0) return true;

    setIsSavingDrafts(true);
    try {
      let catalog: any[] = [];
      try {
        const response = await baseUrl.get("alltreatment/findAllTreatment");
        catalog = response.data.data ?? [];
      } catch (error) {
        console.error(error);
        toast.error("Failed to load treatment catalog");
        return false;
      }

      for (let index = 0; index < draftFormIds.length; index++) {
        const draftId = draftFormIds[index];
        const formData = draftFormDataRef.current[draftId];
        if (!formData) {
          toast.error(`Treatment row ${index + 1}: form data is missing`);
          return false;
        }

        const validation = validateTreatmentForm(
          formData,
          catalog,
          false,
          DEFAULT_MAIN_TREATMENT_STATUS
        );
        if (!validation.valid) {
          toast.error(`Treatment row ${index + 1}: ${validation.message}`);
          return false;
        }

        try {
          await baseUrl.post(
            "treatment/add-treatments",
            {
              ...validation.payload,
              ...appointmentIdPayload(appointmentId),
            },
            {
              headers: { treatmentid: ID },
            }
          );
        } catch (error: any) {
          const message =
            error?.response?.data?.message || "Failed to add treatment";
          toast.error(`Treatment row ${index + 1}: ${message}`);
          return false;
        }
      }

      const savedCount = draftFormIds.length;
      draftFormDataRef.current = {};
      setDraftFormIds([]);
      toast.success(
        savedCount === 1
          ? "Treatment saved"
          : `${savedCount} treatments saved`
      );
      refresh();
      fetchData();
      return true;
    } finally {
      setIsSavingDrafts(false);
    }
  }, [ID, draftFormIds, refresh, fetchData, appointmentId]);

  useEffect(() => {
    registerSavePending?.(saveAllPendingTreatments);
    return () => registerSavePending?.(null);
  }, [registerSavePending, saveAllPendingTreatments]);

  if (showDateWise) {
    return (
      <DateWiseTreatments
        allTeatment={allTeatment}
        setallTeatment={setallTeatment}
        fetchData={fetchData}
        refresh={refresh}
        editingTreatmentId={editingTreatmentId}
        editingTreatment={editingTreatment}
        closeEditForm={closeEditForm}
        handleEditTreatment={handleEditTreatment}
        removediagnose={removediagnose}
        openSubTreatmentId={openSubTreatmentId}
        setOpenSubTreatmentId={setOpenSubTreatmentId}
        setEditingTreatmentId={setEditingTreatmentId}
      />
    );
  }

  return (
    <table className="w-full">
      <thead>
        <tr className="bg-amber-50/80 dark:bg-amber-900/30">
          <th className="px-4 py-3 text-left text-xs font-semibold text-amber-800 dark:text-amber-400 uppercase tracking-wider">Date</th>
          <th className="px-4 py-3 text-left text-xs font-semibold text-amber-800 dark:text-amber-400 uppercase tracking-wider">Treatment</th>
          <th className="px-4 py-3 text-left text-xs font-semibold text-amber-800 dark:text-amber-400 uppercase tracking-wider">Tooth No</th>
          <th className="px-4 py-3 text-left text-xs font-semibold text-amber-800 dark:text-amber-400 uppercase tracking-wider">Qty</th>
          <th className="px-4 py-3 text-left text-xs font-semibold text-amber-800 dark:text-amber-400 uppercase tracking-wider">Price</th>
          <th className="px-4 py-3 text-left text-xs font-semibold text-amber-800 dark:text-amber-400 uppercase tracking-wider">Status</th>
          <th className="px-4 py-3 text-left text-xs font-semibold text-amber-800 dark:text-amber-400 uppercase tracking-wider">Dentist</th>
          <th className="px-4 py-3 text-left text-xs font-semibold text-amber-800 dark:text-amber-400 uppercase tracking-wider">Note</th>
          <th className="px-4 py-3 text-left text-xs font-semibold text-amber-800 dark:text-amber-400 uppercase tracking-wider">Action</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
        {allTeatment?.map((item: any, index: number) => (
          <Fragment key={item._id ?? index}>
            {String(editingTreatmentId) === String(item._id) && editingTreatment ? (
              <TreatmentFormModal
                key={String(item._id)}
                inlineRow
                defaultStatus={DEFAULT_MAIN_TREATMENT_STATUS}
                treatment={allTeatment}
                setaddTreatmentOpent={closeEditForm}
                settreatment={setallTeatment}
                fetchData={fetchData}
                editItem={editingTreatment}
                onClose={closeEditForm}
              />
            ) : (
              <AllMainTreatmentTr
                item={item}
                handleEditTreatment={handleEditTreatment}
                onClick={() => removediagnose(item)}
                refresh={refresh}
                onAddSubTreatment={() => {
                  setEditingTreatmentId(null);
                  setOpenSubTreatmentId((prev) =>
                    prev === item._id ? null : item._id
                  );
                }}
                isSubFormOpen={openSubTreatmentId === item._id}
              />
            )}
            <CaseTreatmentSection
              item={item}
              allTeatment={allTeatment}
              setallTeatment={setallTeatment}
              refresh={refresh}
              showAddForm={openSubTreatmentId === item._id}
              onCloseAddForm={() => setOpenSubTreatmentId(null)}
            />
          </Fragment>
        ))}
        {draftFormIds.map((draftId) => (
          <TreatmentFormModal
            key={draftId}
            inlineRow
            deferSave
            draftId={draftId}
            defaultStatus={DEFAULT_MAIN_TREATMENT_STATUS}
            treatment={allTeatment}
            setaddTreatmentOpent={() => handleDraftRemove(draftId)}
            settreatment={setallTeatment}
            fetchData={fetchData}
            onClose={() => handleDraftRemove(draftId)}
            onDraftChange={handleDraftChange}
            onDraftRemove={handleDraftRemove}
          />
        ))}
        <tr className="hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors">
          <td colSpan={9} className="px-4 py-3">
            <div className="flex flex-wrap items-center justify-center gap-2">
              <button
                type="button"
                onClick={addDraftForm}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-dashed border-indigo-300 dark:border-primary/50 text-indigo-600 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-primary/10 text-xs font-medium transition-colors"
              >
                <AddCircleOutlineIcon sx={{ fontSize: 18 }} />
                Add Treatment
              </button>
              {draftFormIds.length > 0 && (
                <button
                  type="button"
                  onClick={() => void saveAllPendingTreatments()}
                  disabled={isSavingDrafts}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-gradient-to-r from-primary to-primaryLight text-white font-medium text-xs hover:shadow-lg hover:shadow-primary/20 transition-all disabled:opacity-50"
                >
                  <SaveIcon sx={{ fontSize: 16 }} />
                  {isSavingDrafts
                    ? "Saving..."
                    : `Save Treatments (${draftFormIds.length})`}
                </button>
              )}
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  );
};

export default Treatments;
